import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Sparkles } from "lucide-react";
import {
  HAPTIC_PATTERNS,
  vibrate,
  loadHapticsPreference,
  shouldShowHapticsHint,
  markHapticsHintSeen,
} from "@/lib/haptics";
import { toast } from "sonner";

const STEPS = [
  { count: 5, sense: "see", prompt: "Notice five things you can see.", hint: "Colors, shapes, edges. No need to name them out loud." },
  { count: 4, sense: "feel", prompt: "Notice four things you can feel.", hint: "The floor under you. Fabric on skin. The weight of your hands." },
  { count: 3, sense: "hear", prompt: "Notice three things you can hear.", hint: "Near sounds, far sounds. Even the quiet has a texture." },
  { count: 2, sense: "smell", prompt: "Notice two things you can smell.", hint: "If nothing arrives, just breathe in slowly. That counts." },
  { count: 1, sense: "taste", prompt: "Notice one thing you can taste.", hint: "The inside of your mouth. Water. A slow breath." },
];

const DELAY_BEFORE_NEXT_MS = 3500;

export const GroundingFlow = ({ open, onClose }) => {
  const [idx, setIdx] = useState(0);
  const [canAdvance, setCanAdvance] = useState(false);

  useEffect(() => {
    if (!open) {
      setIdx(0);
      setCanAdvance(false);
      return;
    }
    setCanAdvance(false);
    const t = setTimeout(() => setCanAdvance(true), DELAY_BEFORE_NEXT_MS);
    // First-time gentle hint that haptics is on (only on touch/small screens).
    if (idx === 0 && shouldShowHapticsHint()) {
      toast("gentle taps are on", {
        description:
          "you'll feel a soft cue when you move to the next step. you can turn them off in the breathing exercise.",
        duration: 6500,
      });
      markHapticsHintSeen();
    }
    return () => clearTimeout(t);
  }, [open, idx]);

  const next = () => {
    // Read the latest haptics preference at click time so the setting in the
    // breathing overlay is always respected here too (they share one key).
    const hapticsOn = loadHapticsPreference();
    if (idx < STEPS.length - 1) {
      if (hapticsOn) vibrate(HAPTIC_PATTERNS.step);
      setIdx(idx + 1);
    } else {
      if (hapticsOn) vibrate(HAPTIC_PATTERNS.arrive);
      onClose();
    }
  };

  if (!open) return null;

  const step = STEPS[idx];
  const isLast = idx === STEPS.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        key="grounding-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.2, ease: "easeInOut" }}
        className="fixed inset-0 z-30 overflow-y-auto overscroll-contain"
        data-testid="grounding-overlay"
      >
        {/* dim veil */}
        <div className="fixed inset-0 bg-black/40 backdrop-blur-2xl pointer-events-none" aria-hidden="true" />

        <button
          onClick={onClose}
          className="fixed w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 backdrop-blur-xl text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-500 z-10"
          style={{
            top: "calc(env(safe-area-inset-top) + 1rem)",
            right: "calc(env(safe-area-inset-right) + 1rem)",
          }}
          data-testid="grounding-close-btn"
          aria-label="Close grounding exercise"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        <div className="relative min-h-full flex items-center justify-center px-6 py-24 sm:py-20">
        <div className="relative w-full max-w-xl text-center">
          {/* Breathing orb behind the number */}
          <motion.div
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div
              className="breathing-orb w-64 h-64 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(167,139,250,0.55) 0%, rgba(34,211,238,0.15) 45%, transparent 75%)",
                filter: "blur(30px)",
              }}
            />
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -8, filter: "blur(6px)" }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
              className="relative flex flex-col items-center gap-6 px-4 sm:px-6 py-8 sm:py-12"
              data-testid={`grounding-step-${step.count}`}
            >
              <div className="font-heading text-white/95 text-[7rem] sm:text-[9rem] leading-none drop-shadow-lg">
                {step.count}
              </div>
              <h2 className="font-heading italic text-white/90 text-2xl sm:text-3xl">
                {step.prompt}
              </h2>
              <p className="text-white/60 text-sm sm:text-base max-w-md leading-relaxed">
                {step.hint}
              </p>

              <motion.button
                onClick={next}
                disabled={!canAdvance}
                initial={{ opacity: 0 }}
                animate={{ opacity: canAdvance ? 1 : 0 }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                whileHover={{ scale: canAdvance ? 1.04 : 1 }}
                whileTap={{ scale: canAdvance ? 0.98 : 1 }}
                className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3 bg-white/10 hover:bg-white/20 border border-white/15 text-white/90 backdrop-blur-xl disabled:cursor-not-allowed"
                data-testid="grounding-next-btn"
                aria-label={isLast ? "Finish grounding" : "Continue to next step"}
              >
                {isLast ? (
                  <>
                    <Sparkles size={16} strokeWidth={1.5} />
                    <span>you are here</span>
                  </>
                ) : (
                  <>
                    <span>when you are ready</span>
                    <ArrowRight size={16} strokeWidth={1.5} />
                  </>
                )}
              </motion.button>

              <div
                className="mt-4 flex items-center gap-2"
                data-testid="grounding-progress"
              >
                {STEPS.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1 rounded-full transition-all duration-700 ${
                      i === idx
                        ? "w-8 bg-white/80"
                        : i < idx
                          ? "w-4 bg-white/40"
                          : "w-4 bg-white/15"
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GroundingFlow;
